import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Repeat, Calendar } from 'lucide-react';

export default function RepeatOrderPage() {
  const { t, getProductName } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    if (user?.company_id) fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    const res = await axios.get(`${API}/orders?company_id=${user.company_id}`);
    setOrders(res.data);
  };

  const handleRepeat = async (order) => {
    setSubmitting(order.id);
    try {
      const items = order.order_items.map(i => ({
        product_id: i.product_id,
        ordered_quantity: i.ordered_quantity
      }));
      const res = await axios.post(`${API}/orders`, {
        delivery_location_id: order.delivery_location_id,
        items,
        notes: `Repeat of ${order.order_number}`
      });
      toast.success(`Order ${res.data.order_number} created!`);
      navigate('/client/orders');
    } catch (err) {
      toast.error('Failed');
    } finally {
      setSubmitting(null);
    }
  };

  const formatCurrency = (val) => `₩${Number(val).toLocaleString('ko-KR')}`;

  return (
    <div className="space-y-6" data-testid="repeat-order-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('repeatOrder')}</h1>
        <p className="text-gray-500 mt-1">Click to repeat any previous order</p>
      </div>

      {orders.length === 0 ? (
        <Card className="p-12 text-center">
          <Repeat className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No previous orders to repeat</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map(o => (
            <Card key={o.id} className="p-5 cff-hover-lift" data-testid={`repeat-card-${o.order_number}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-xs text-gray-500">{o.order_number}</span>
                    <span className="text-xs text-gray-400">·</span>
                    <span className="text-xs text-gray-500 cff-text-numeric">
                      {new Date(o.order_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700">
                    {o.order_items?.slice(0, 3).map(i => (
                      <span key={i.id} className="mr-2">
                        {getProductName(i.product)} × {i.ordered_quantity}
                      </span>
                    ))}
                    {o.order_items?.length > 3 && <span className="text-gray-400">+{o.order_items.length - 3} more</span>}
                  </div>
                  <p className="font-bold cff-text-numeric mt-1 text-gray-900">{formatCurrency(o.total_amount)}</p>
                </div>
                <Button
                  onClick={() => handleRepeat(o)}
                  disabled={submitting === o.id}
                  className="cff-button-primary"
                  data-testid={`repeat-btn-${o.order_number}`}
                >
                  <Repeat className="h-4 w-4 mr-2" />
                  {submitting === o.id ? '...' : t('repeatOrder')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
